/**
 * The four demo scene kinds. Each renders inside the 1920x1080 design stage;
 * the camera/cursor math runs in captured-page coordinates (see camera.ts).
 */
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion'
import { clamp01, easeOutExpo, easeOutQuint } from '@/lib/easing'
import { resolveCamera, resolveCursor, toFrame } from './camera'
import { alpha, demoFonts, type DemoPalette } from './theme'
import { BrowserChrome, CHROME_BAR_H, Cursor, DecodedImg, TextBlock, riseStyle } from './ui'
import { DEMO_STAGE_H, DEMO_STAGE_W, type DemoScenePayload } from './types'

export function TitleScene({ scene, pal }: { scene: DemoScenePayload; pal: DemoPalette }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const glow = easeOutExpo(clamp01(frame / fps / 1.4))
  return (
    <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{
          position: 'absolute',
          width: 1100,
          height: 520,
          borderRadius: '50%',
          background: `radial-gradient(50% 50% at 50% 50%, ${alpha(pal.accent, 0.22 * glow)}, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 30, textAlign: 'center', padding: '0 120px' }}>
        {scene.badge ? (
          <div style={riseStyle(frame, fps, 0.1)}>
            <span
              style={{
                fontFamily: demoFonts.mono,
                fontSize: 17,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: pal.accent,
                border: `1px solid ${alpha(pal.accent, 0.35)}`,
                background: alpha(pal.accent, 0.08),
                borderRadius: 999,
                padding: '10px 22px',
              }}
            >
              {scene.badge}
            </span>
          </div>
        ) : null}
        <h1
          style={{
            ...riseStyle(frame, fps, 0.24, 34),
            fontFamily: demoFonts.headline,
            fontWeight: 700,
            fontSize: 132,
            lineHeight: 1.0,
            letterSpacing: '-0.035em',
            color: pal.text,
            margin: 0,
            maxWidth: 1500,
          }}
        >
          {scene.headline}
        </h1>
        {scene.sub ? (
          <p
            style={{
              ...riseStyle(frame, fps, 0.42, 24),
              fontFamily: demoFonts.serif,
              fontStyle: 'italic',
              fontSize: 40,
              color: pal.textDim,
              margin: 0,
              maxWidth: 1000,
              lineHeight: 1.25,
            }}
          >
            {scene.sub}
          </p>
        ) : null}
      </div>
    </AbsoluteFill>
  )
}

export function BrowseScene({ scene, pal }: { scene: DemoScenePayload; pal: DemoPalette }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = frame / fps
  const shotSize = scene.shotSize ?? { w: 1920, h: 1080 }

  const hasText = Boolean(scene.headline || scene.label)
  const pad = 64
  const textH = hasText ? 178 : 0
  const winW = Math.round(DEMO_STAGE_W - pad * 2)
  const winH = Math.round(DEMO_STAGE_H - pad - textH - 36)
  const contentW = winW - 2
  const contentH = winH - CHROME_BAR_H - 2

  const cam = resolveCamera(scene.camera, t, contentW, contentH, shotSize.w, shotSize.h)
  const cursor = resolveCursor(scene.cursor, t)
  const cursorFrame = toFrame(cursor, cam)
  const enter = easeOutExpo(clamp01(t / 0.9))

  return (
    <AbsoluteFill>
      {hasText ? (
        <div style={{ position: 'absolute', left: pad, top: 54 }}>
          <TextBlock pal={pal} label={scene.label} headline={scene.headline} sub={scene.sub} />
        </div>
      ) : null}
      <div
        style={{
          position: 'absolute',
          left: pad,
          top: textH + 36,
          opacity: enter,
          transform: `translateY(${(1 - enter) * 44}px) scale(${0.985 + enter * 0.015})`,
        }}
      >
        <BrowserChrome pal={pal} url={scene.url} width={winW} height={winH}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: `translate(${cam.tx}px, ${cam.ty}px) scale(${cam.scale})`,
              transformOrigin: '0 0',
              width: shotSize.w,
              height: shotSize.h,
            }}
          >
            {scene.shot ? (
              <DecodedImg src={scene.shot} style={{ width: shotSize.w, height: shotSize.h, display: 'block' }} />
            ) : null}
            {scene.calloutRect ? (
              <div
                style={{
                  position: 'absolute',
                  left: scene.calloutRect.x - 10,
                  top: scene.calloutRect.y - 10,
                  width: scene.calloutRect.w + 20,
                  height: scene.calloutRect.h + 20,
                  borderRadius: 12,
                  border: `${2.5 / cam.scale}px solid ${alpha(pal.accent, 0.9)}`,
                  boxShadow: `0 0 0 ${6 / cam.scale}px ${alpha(pal.accent, 0.18)}, 0 0 ${36 / cam.scale}px ${alpha(pal.accent, 0.35)}`,
                  opacity: easeOutExpo(clamp01((t - 0.7) / 0.6)),
                }}
              />
            ) : null}
          </div>
          {cursor.visible ? (
            <Cursor x={cursorFrame.x} y={cursorFrame.y} press={cursor.press} click={cursor.click} accent={pal.accent} />
          ) : null}
        </BrowserChrome>
      </div>
    </AbsoluteFill>
  )
}

export function TiltScene({ scene, pal }: { scene: DemoScenePayload; pal: DemoPalette }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = frame / fps
  const dir = scene.direction === 'right' ? -1 : 1
  const dolly = 1.02 + easeOutQuint(clamp01(t / 7)) * 0.07
  const hasText = Boolean(scene.headline || scene.label)
  const shots = scene.shots ?? []

  const cardW = shots.length > 1 ? 1150 : 1380
  const cards = shots.map((shot, i) => {
    const enter = easeOutExpo(clamp01((t - 0.12 - i * 0.16) / 1.1))
    const float = Math.sin(t * 0.7 + i * 1.9) * 9
    return { shot, i, enter, float }
  })

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          perspective: 1700,
        }}
      >
        <div
          style={{
            transformStyle: 'preserve-3d',
            transform: `scale(${dolly}) rotateX(12deg) rotateY(${-16 * dir}deg) rotateZ(${2 * dir}deg)`,
          }}
        >
          {cards.map(({ shot, i, enter, float }) => (
            <div
              key={shot + i}
              style={{
                position: i === 0 ? 'relative' : 'absolute',
                left: i === 0 ? 0 : i * 170 * dir - 40 * dir,
                top: i === 0 ? 0 : i * 120 - 30,
                width: cardW,
                transform: `translateZ(${-i * 190}px) translateY(${float + (1 - enter) * 90}px)`,
                opacity: enter,
                borderRadius: 16,
                overflow: 'hidden',
                border: `1px solid ${alpha('#ffffff', 0.14)}`,
                boxShadow: `0 60px 120px -30px ${alpha('#000000', 0.65)}, 0 0 80px ${alpha(pal.accent, i === 0 ? 0.14 : 0.05)}`,
                background: pal.bgRaised,
              }}
            >
              <DecodedImg src={shot} style={{ width: '100%', display: 'block' }} />
              {i === 0 ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(115deg, transparent ${30 + ((t * 26) % 170)}%, ${alpha('#ffffff', 0.10)} ${38 + ((t * 26) % 170)}%, transparent ${47 + ((t * 26) % 170)}%)`,
                  }}
                />
              ) : null}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  border: `1px solid ${alpha('#ffffff', 0.08)}`,
                  borderRadius: 16,
                  background: `linear-gradient(180deg, ${alpha('#ffffff', 0.05)}, transparent 18%)`,
                }}
              />
            </div>
          ))}
        </div>
      </div>
      {hasText ? (
        <div style={{ position: 'absolute', left: 76, bottom: 70 }}>
          <TextBlock pal={pal} label={scene.label} headline={scene.headline} sub={scene.sub} delaySec={0.5} />
        </div>
      ) : null}
    </AbsoluteFill>
  )
}

export function OutroScene({ scene, pal }: { scene: DemoScenePayload; pal: DemoPalette }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const t = frame / fps
  const bloom = easeOutExpo(clamp01(t / 1.6))
  return (
    <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{
          position: 'absolute',
          width: 900,
          height: 480,
          borderRadius: '50%',
          background: `radial-gradient(50% 50% at 50% 50%, ${alpha(pal.accent, 0.26 * bloom)}, transparent 70%)`,
          filter: 'blur(46px)',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 34, textAlign: 'center' }}>
        <h1
          style={{
            ...riseStyle(frame, fps, 0.1, 30),
            fontFamily: demoFonts.headline,
            fontWeight: 700,
            fontSize: 116,
            letterSpacing: '-0.035em',
            color: pal.text,
            margin: 0,
            maxWidth: 1400,
            lineHeight: 1.02,
          }}
        >
          {scene.headline}
        </h1>
        {scene.sub ? (
          <p
            style={{
              ...riseStyle(frame, fps, 0.26, 22),
              fontFamily: demoFonts.serif,
              fontStyle: 'italic',
              fontSize: 36,
              color: pal.textDim,
              margin: 0,
            }}
          >
            {scene.sub}
          </p>
        ) : null}
        <div style={{ ...riseStyle(frame, fps, 0.42, 20), display: 'flex', alignItems: 'center', gap: 26 }}>
          {scene.cta ? (
            <span
              style={{
                fontFamily: demoFonts.headline,
                fontWeight: 600,
                fontSize: 27,
                color: '#fff',
                background: pal.accent,
                borderRadius: 999,
                padding: '18px 42px',
                boxShadow: `0 14px 44px -10px ${alpha(pal.accent, 0.7)}`,
              }}
            >
              {scene.cta}
            </span>
          ) : null}
          {scene.site ? (
            <span style={{ fontFamily: demoFonts.mono, fontSize: 23, color: pal.textDim, letterSpacing: '0.04em' }}>
              {scene.site}
            </span>
          ) : null}
        </div>
      </div>
    </AbsoluteFill>
  )
}
